/**
 * @module app.cd
 */

import { bindingMode, autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { LookupService } from 'app/cd/services/lookup-service';
import { LookupService as CoreLookupService } from 'app/core/services/lookup-service';
import { IMemberSearchFilters } from "app/cd/services/member-service";
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { apiCall } from 'app/shared/lib/api/api';
import { MemberFilters } from 'app/shared/config/modules';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import { NamespaceOptions } from 'app/shared/lib/i18n';

export type AvailableFilters = 'active' | 'identifier.primary' | 'identifier.medicaid' | 'name.first' | 'name.last' | 'birthDate' |
    'primaryCarePhysician' | 'primaryCarePhysician.name.first' | 'primaryCarePhysician.name.last' | 'healthPlan' | 'household.name' |
    'household.id' | 'lineOfBusiness' | 'birthDateBetween' | 'identifier.socialSecurityNumber' | 'gender' | 'address.city'

@autoinject
export class MemberFiltersCustomElement extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public searchFilters: IMemberSearchFilters = {};

    @bindable.objectNull()
    public availableFilters: AvailableFilters[] | null = [
        'active', 'identifier.primary', 'identifier.medicaid', 'name.first', 'name.last', 'birthDate',
        'primaryCarePhysician', 'primaryCarePhysician.name.first', 'primaryCarePhysician.name.last',
        'household.name', 'household.id', 'healthPlan', 'lineOfBusiness'
    ];

    @bindable.stringNull()
    public requiredFilters: string | null = null;

    @bindable.stringNull()
    public module: string | null = null;

    @bindable.string()
    public i18nNamespace: Extract<NamespaceOptions, NamespaceOptions.CD | NamespaceOptions.PP> = NamespaceOptions.CD;

    protected hasHouseholdCaseRoute: boolean = false;

    public healthPlans: ReadonlyArray<CD.HealthPlan> | null = [];
    public linesOfBusiness: ReadonlyArray<CD.LineOfBusiness> | null = [];
    public genders: ReadonlyArray<Core.Gender> | null = [];

    @bindable.string()
    public ariaLabelledbyId: string = '';

    protected maxMemberIdLength: number = 20;
    protected maxMedicaidIdLength: number = 20;
    protected maxMemberFirstNameLength: number = 50;
    protected maxMemberLastNameLength: number = 50;
    protected maxMemberSSNLength: number = 11;
    protected maxPCPIdLength: number = 20;
    protected maxPCPFirstNameLength: number = 50;
    protected maxPCPLastNameLength: number = 50;
    protected maxHouseholdNameLength: number = 100;
    protected maxHouseholdIdLength: number = 50;
    protected maxMemberCityLength: number = 30;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly service: LookupService,
        protected readonly coreService: CoreLookupService,
        protected readonly memberFilters: MemberFilters,
        ...rest: any[]) {
        super(...rest);
        this.checkPermissions();
    }

    protected bind(): void {
        this.loadLookups();
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasHouseholdCaseRoute = this.accountContext.hasRouteAccess('cm.household-case');
    }

    protected filterVisible(filterName: AvailableFilters): boolean {
        if(filterName.includes('household') && this.module === 'um') {
            return false;
        };

        let filter = this.memberFilters.getFilter(filterName);

        if(filter) {
            if(this.module && this.module !== filter.moduleCode) {
                let index = filter.showInModules ? filter.showInModules.indexOf(this.module) : -1;
                if(index < 0) { return false; }
            }
        }

        if(this.availableFilters) {
            let index = this.availableFilters.indexOf(filterName);
            if(index < 0) { return false; }
        }

        return true;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const fields = ['description'];

        try {
            this.state.beginLoad();
            const [responseHealthPlan, responseLoB, responseGender] = await Promise.all(
                [
                    this.filterVisible('healthPlan') ? this.service.loadHealthPlans(options, undefined, fields) : null,
                    this.filterVisible('lineOfBusiness') ? this.service.loadLinesOfBusiness(options, undefined, fields) : null,
                    this.filterVisible('gender') ? this.coreService.loadGenders(options, undefined, undefined, true, fields) : null
                ]
            );

            this.healthPlans = responseHealthPlan?.results || [];
            this.linesOfBusiness = responseLoB?.results || [];
            this.genders = responseGender?.results || [];
        }
        catch(error) {
            this.logger.error('Error Loading Lookups', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
